import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CloseIcon from "../../assets/svg/close-icon";
import ChevronLeftIcon from "../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../assets/svg/chevron-right-icon";
import VfCopilotIcon from "../../assets/svg/vf-copilot-icon";
import AnnouncementBannerBg from "../../assets/images/bg-announcement-banner.png";
import { AnnouncementSlide } from "../../utils/constants/announcementBannerInfo";

type AnnouncementBannerProps = {
  slides: AnnouncementSlide[];
  onDismiss: () => void;
};

type SlideDirection = "next" | "prev";

// Ticker auto-advances between slides; hovering the bar pauses it (see effect below).
const AUTO_ROTATE_INTERVAL_MS = 6000;
// Must match the .animate-ticker-* durations in theme-config.css (0.8s) — used to unmount the
// outgoing slide once it has finished fading/sliding off.
const SLIDE_ANIMATION_MS = 800;

// Shared "Learn More" styling — DM Sans SemiBold 14px, underlined, white (Figma "Component 29").
const CTA_CLASS_NAME =
  "text-[14px] font-semibold underline whitespace-nowrap shrink-0 text-white";

// One slide's content: optional sparkle icon + message + divider + "Learn More" CTA. The CTA is an
// in-app <Link> (internal) or a new-tab <a> (external), matching Figma exactly for both slides.
const SlideContent = ({ slide }: { slide: AnnouncementSlide }) => (
  <div className="flex items-center gap-4.5 text-white max-w-[70%]">
    <div className="flex items-center gap-1.5 min-w-0">
      {slide.showIcon && (
        <VfCopilotIcon className="w-4.5 h-4.5 shrink-0" />
      )}
      <p
        className="text-[14px] font-normal tracking-[-0.14px] truncate"
        dangerouslySetInnerHTML={{ __html: slide.messageHtml }}
      />
    </div>
    <span className="h-3.5 w-px bg-white/40 mix-blend-overlay shrink-0" />
    {slide.cta.kind === "external" ? (
      <a
        href={slide.cta.url}
        target="_blank"
        rel="noopener noreferrer"
        className={CTA_CLASS_NAME}
      >
        {slide.ctaText}
      </a>
    ) : (
      <Link to={slide.cta.to} className={CTA_CLASS_NAME}>
        {slide.ctaText}
      </Link>
    )}
  </div>
);

const AnnouncementBanner = ({ slides, onDismiss }: AnnouncementBannerProps) => {
  const slideCount = slides.length;
  const isCarousel = slideCount > 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // The slide currently sliding off-screen (and which way we're travelling). Cleared once its
  // animation finishes. Direction is tracked explicitly so the auto-rotate wrap (last -> first)
  // still slides forward instead of visibly rocking backwards.
  const [outgoing, setOutgoing] = useState<{
    index: number;
    direction: SlideDirection;
  } | null>(null);

  const changeSlide = useCallback(
    (direction: SlideDirection) => {
      setActiveIndex((current) => {
        const nextIndex =
          direction === "next"
            ? (current + 1) % slideCount
            : (current - 1 + slideCount) % slideCount;
        setOutgoing({ index: current, direction });
        return nextIndex;
      });
    },
    [slideCount]
  );

  // Reset when the slide set changes (e.g. VerbaIQ access resolves after login).
  useEffect(() => {
    setActiveIndex(0);
    setOutgoing(null);
  }, [slideCount]);

  // Auto-advance forward, paused on hover.
  useEffect(() => {
    if (!isCarousel || isPaused) return;
    const timer = setInterval(() => changeSlide("next"), AUTO_ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isCarousel, isPaused, changeSlide]);

  // Unmount the outgoing slide once it has slid off (timeout, not animationend, so it also resolves
  // in jsdom where CSS animations don't run).
  useEffect(() => {
    if (!outgoing) return;
    const timer = setTimeout(() => setOutgoing(null), SLIDE_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [outgoing]);

  if (slideCount === 0) return null;

  const enterClass = outgoing
    ? outgoing.direction === "next"
      ? "animate-ticker-in-from-right"
      : "animate-ticker-in-from-left"
    : "";
  const exitClass =
    outgoing?.direction === "next"
      ? "animate-ticker-out-to-left"
      : "animate-ticker-out-to-right";

  return (
    <div
      className="relative h-8 overflow-hidden"
      style={{
        background: `linear-gradient(90deg, #3D61FF 0%, #4C97FF 100%)`,
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <img
        src={AnnouncementBannerBg}
        alt=""
        className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-70"
      />

      {/* Outgoing slide — sliding off-screen, hidden from a11y so only the active CTA is exposed. */}
      {outgoing && (
        <div
          key={`out-${outgoing.index}`}
          aria-hidden
          className={`absolute inset-0 z-10 flex items-center justify-center px-3 pointer-events-none ${exitClass}`}
        >
          <SlideContent slide={slides[outgoing.index]} />
        </div>
      )}

      {/* Active slide — re-keyed on index so the enter animation replays on every change. */}
      <div
        key={`active-${activeIndex}`}
        className={`absolute inset-0 z-10 flex items-center justify-center px-3 ${enterClass}`}
      >
        <SlideContent slide={slides[activeIndex]} />
      </div>

      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex items-center gap-4.5 text-white">
        {isCarousel && (
          <>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => changeSlide("prev")}
                aria-label="Previous announcement"
                className="w-4.5 h-4.5 rounded-full flex items-center justify-center bg-black/15 hover:bg-black/25 transition-colors"
              >
                <ChevronLeftIcon className="w-4.5 h-4.5 text-white" />
              </button>
              <span className="text-[10px] font-normal tracking-[-0.1px] tabular-nums leading-none">
                {`${activeIndex + 1} / ${slideCount}`}
              </span>
              <button
                type="button"
                onClick={() => changeSlide("next")}
                aria-label="Next announcement"
                className="w-4.5 h-4.5 rounded-full flex items-center justify-center bg-black/15 hover:bg-black/25 transition-colors"
              >
                <ChevronRightIcon className="w-4.5 h-4.5 text-white" />
              </button>
            </div>
            <span className="h-2 w-px bg-white/40 mix-blend-overlay" />
          </>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
          className="w-4.5 h-4.5 flex items-center justify-center"
        >
          <CloseIcon className="w-3 h-3 text-white" strokeWidth={1.2} />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
